import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "fixed inset-0 flex" }
const _hoisted_2 = { class: "absolute left-full top-0 flex w-16 justify-center pt-5" }
const _hoisted_3 = { class: "flex grow flex-col gap-y-5 overflow-y-auto bg-dbBlue px-6 pb-4 dark:bg-black/10 dark:ring-1 dark:ring-white/10" }
const _hoisted_4 = { class: "flex h-16 shrink-0 items-center" }
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = { class: "flex flex-1 flex-col" }
const _hoisted_7 = {
  role: "list",
  class: "flex flex-1 flex-col gap-y-7"
}
const _hoisted_8 = {
  role: "list",
  class: "-mx-2 space-y-1"
}
const _hoisted_9 = { class: "mt-auto" }
const _hoisted_10 = {
  role: "list",
  class: "-mx-2 mt-2 space-y-1"
}
const _hoisted_11 = ["href"]
const _hoisted_12 = { class: "flex grow flex-col gap-y-5 bg-dbBlue px-6 pb-4 dark:bg-black/10 dark:ring-1 dark:ring-white/5 group/sidebar relative" }
const _hoisted_13 = { class: "flex h-16 shrink-0 items-center" }
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = ["src", "alt"]
const _hoisted_16 = { class: "flex flex-1 flex-col" }
const _hoisted_17 = {
  role: "list",
  class: "flex flex-1 flex-col gap-y-7"
}
const _hoisted_18 = {
  role: "list",
  class: "-mx-2 space-y-1"
}
const _hoisted_19 = { class: "mt-auto" }
const _hoisted_20 = { class: "text-xs font-semibold uppercase tracking-wide text-indigo-300 dark:text-gray-500" }
const _hoisted_21 = {
  role: "list",
  class: "-mx-2 mt-2 space-y-1"
}
const _hoisted_22 = ["href"]
const _hoisted_23 = { class: "sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 dark:border-white/5 dark:bg-gray-900" }
const _hoisted_24 = { class: "flex flex-1 gap-x-4 self-stretch justify-end lg:gap-x-6" }
const _hoisted_25 = { class: "flex items-center gap-x-4 lg:gap-x-6" }
const _hoisted_26 = { class: "h-8 w-8 rounded-full bg-gray-300 flex justify-center items-center" }
const _hoisted_27 = { class: "hidden lg:flex lg:items-center" }
const _hoisted_28 = {
  class: "ml-4 text-sm font-semibold leading-6 text-gray-900 dark:text-white",
  "aria-hidden": "true"
}
const _hoisted_29 = { class: "py-5 bg-gray-50 dark:bg-gray-900 min-h-screen" }
const _hoisted_30 = { class: "px-4 sm:px-6 lg:px-8" }

import {
		Dialog,
		DialogPanel,
		Menu,
		MenuButton,
		MenuItem,
		MenuItems,
		TransitionChild,
		TransitionRoot,
	} from '@headlessui/vue'
	import WebinarModal from '@/components/Modals/WebinarModal.vue'
	import NewsletterModal from '@/components/Modals/NewsletterModal.vue'

	// icons
	import {
		Bars3Icon,
		EnvelopeIcon,
		NewspaperIcon,
		PlayCircleIcon,
		XMarkIcon,
	} from '@heroicons/vue/24/outline'
	import {
		ChevronDownIcon,
		UserIcon,
		ChevronLeftIcon as ChevronLeftIconMini
	} from '@heroicons/vue/20/solid'

	// store
	import { 
		store as _systemStore
	} from '@/store/system'

	import {
		store as _userStore
	} from '@/store/user'

	// utilities
	import {
		showsLicensing
	} from '@/utilities/typeOfWork'

	import {
		fetchLicenses
	} from '@/modules/Licensing/queries'

	import {
		useBrand
	} from '@/utilities/brand'

	// vue
	import {
		computed,
		onMounted,
		ref
	} from 'vue'

	
	const __default__ = { name: 'SidebarWithHeader' } // name the component

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

	// components
	const systemStore = _systemStore()
	const userStore = _userStore()
	const brand = useBrand()

	// load file paths of all modules named Nav.vue
	const context = require.context('@/modules', true, /Nav.ts$/i)

	// import each component
	const modules = context.keys()
		.map(context)
		.map((m: any) => m.nav) 

	const typeOfWork = userStore?.user?.cognitoUser?.attributes?.["custom:typeOfWork"]

	// only looked up for customers without FM, who would otherwise never see the menu item
	const hasLicenses = ref(false)

	const showLicensing = computed(() => showsLicensing(typeOfWork, hasLicenses.value))

	// sort them based on the position data prop. computed, so Licensing appears once the license
	// lookup below comes back
	const navigation = computed(() => modules.filter((m) => {
		if(!showLicensing.value && m.name === 'Licensing') {
			return false
		}

		return m.show
	}).sort((a, b) => {
		let aPos = a.position
		let bPos = b.position
		if(aPos < bPos) {
			return -1
		}
		else if (aPos > bPos) {
			return 1
		}
		else {
			return 0
		}
	}))

	// customers with FM already see Licensing, so only the rest pay for a FileMaker lookup. a failed
	// lookup leaves the menu item hidden, as it was before the lookup existed
	onMounted(async () => {
		if (showLicensing.value) return

		try {
			hasLicenses.value = (await fetchLicenses()).length > 0
		}
		catch (e) {
			console.error(e)
		}
	})

	const userNavigation = [
		{ name: 'My Profile', to: {name: 'MyProfile'} },
		{ name: 'Log out', to: {name: 'SignOut'} },
	]
  
	const sidebarOpen = ref(false)

	const webinarModal = ref<{open: () => void} | null>(null)
	const newsletterModal = ref<{open: () => void} | null>(null)

	// open the latest webinar modal from the sidebar's Additional Resources section
	function openWebinarModal() {
		webinarModal.value?.open()
	}

	// open the newsletter signup modal from the sidebar's Additional Resources section
	function openNewsletterModal() {
		newsletterModal.value?.open()
	}

	const displayName = computed(() => {
		return [userStore?.user?.cognitoUser?.attributes?.given_name, userStore?.user?.cognitoUser?.attributes?.family_name].filter(Boolean).join(" ")
	})

	function collapseSidebar() {
		systemStore.system.sidebarExpanded = !systemStore.system.sidebarExpanded
	}
  
return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(TransitionRoot), {
      as: "template",
      show: sidebarOpen.value
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Dialog), {
          as: "div",
          class: "relative z-40 lg:hidden",
          onClose: _cache[3] || (_cache[3] = ($event: any) => (sidebarOpen.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(TransitionChild), {
              as: "template",
              enter: "transition-opacity ease-linear duration-300",
              "enter-from": "opacity-0",
              "enter-to": "opacity-100",
              leave: "transition-opacity ease-linear duration-300",
              "leave-from": "opacity-100",
              "leave-to": "opacity-0"
            }, {
              default: _withCtx(() => _cache[8] || (_cache[8] = [
                _createElementVNode("div", { class: "fixed inset-0 bg-gray-900/80" }, null, -1)
              ])),
              _: 1,
              __: [8]
            }),
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_unref(TransitionChild), {
                as: "template",
                enter: "transition ease-in-out duration-300 transform",
                "enter-from": "-translate-x-full",
                "enter-to": "translate-x-0",
                leave: "transition ease-in-out duration-300 transform",
                "leave-from": "translate-x-0",
                "leave-to": "-translate-x-full"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogPanel), { class: "relative mr-16 flex w-full max-w-xs flex-1 dark:bg-gray-900" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TransitionChild), {
                        as: "template",
                        enter: "ease-in-out duration-300",
                        "enter-from": "opacity-0",
                        "enter-to": "opacity-100",
                        leave: "ease-in-out duration-300",
                        "leave-from": "opacity-100",
                        "leave-to": "opacity-0"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "-m-2.5 p-2.5",
                              onClick: _cache[0] || (_cache[0] = ($event: any) => (sidebarOpen.value = false))
                            }, [
                              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "sr-only" }, "Close sidebar", -1)),
                              _createVNode(_unref(XMarkIcon), {
                                class: "h-6 w-6 text-white",
                                "aria-hidden": "true"
                              })
                            ])
                          ])
                        ]),
                        _: 1
                      }),
                      _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                          _createElementVNode("img", {
                            class: _normalizeClass([_unref(brand).logos.sidebar.class, 'left-0']),
                            src: _unref(brand).logos.sidebar.src,
                            alt: _unref(brand).logos.sidebar.alt
                          }, null, 10, _hoisted_5)
                        ]),
                        _createElementVNode("nav", _hoisted_6, [
                          _createElementVNode("ul", _hoisted_7, [
                            _createElementVNode("li", null, [
                              _createElementVNode("ul", _hoisted_8, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navigation.value, (item) => {
                                  return (_openBlock(), _createElementBlock("li", {
                                    key: item.name
                                  }, [
                                    _createVNode(_component_router_link, {
                                      to: item.to,
                                      class: _normalizeClass([_ctx.$route.meta.module === item.module ? 'bg-indigo-900 text-white dark:bg-gray-800' : 'text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800', 'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'])
                                    }, {
                                      default: _withCtx(() => [
                                        (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), {
                                          class: _normalizeClass([_ctx.$route.meta.module === item.module ? 'text-white' : 'text-indigo-200 group-hover:text-white dark:text-gray-400', 'h-6 w-6 shrink-0']),
                                          "aria-hidden": "true"
                                        }, null, 8, ["class"])),
                                        _createTextVNode(" " + _toDisplayString(item.name), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["to", "class"])
                                  ]))
                                }), 128))
                              ])
                            ]),
                            _createElementVNode("li", _hoisted_9, [
                              _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-indigo-300 dark:text-gray-500" }, "Additional Resources", -1)),
                              _createElementVNode("ul", _hoisted_10, [
                                _createElementVNode("li", null, [
                                  _createElementVNode("a", {
                                    href: _unref(brand).blogUrl,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    class: "group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                                  }, [
                                    _createVNode(_unref(NewspaperIcon), {
                                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                                      "aria-hidden": "true"
                                    }),
                                    _cache[10] || (_cache[10] = _createTextVNode(" Read Our Blog "))
                                  ], 8, _hoisted_11)
                                ]),
                                _createElementVNode("li", null, [
                                  _createElementVNode("button", {
                                    type: "button",
                                    onClick: _cache[1] || (_cache[1] = ($event: any) => (openWebinarModal())),
                                    class: "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                                  }, [
                                    _createVNode(_unref(PlayCircleIcon), {
                                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                                      "aria-hidden": "true"
                                    }),
                                    _cache[11] || (_cache[11] = _createTextVNode(" View Our Latest Webinar "))
                                  ])
                                ]),
                                _createElementVNode("li", null, [
                                  _createElementVNode("button", {
                                    type: "button",
                                    onClick: _cache[2] || (_cache[2] = ($event: any) => (openNewsletterModal())),
                                    class: "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                                  }, [
                                    _createVNode(_unref(EnvelopeIcon), {
                                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                                      "aria-hidden": "true"
                                    }),
                                    _cache[12] || (_cache[12] = _createTextVNode(" Subscribe to Our Newsletter "))
                                  ])
                                ])
                              ])
                            ])
                          ])
                        ])
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["show"]),
    _createElementVNode("div", {
      class: _normalizeClass([_unref(systemStore).system.sidebarExpanded ? 'lg:w-72' : 'lg:w-[72px]', 'hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:flex-col dark:bg-gray-900 transition-all duration-200 ease-in-out'])
    }, [
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _withDirectives(_createElementVNode("img", {
            class: _normalizeClass([_unref(brand).logos.sidebar.class, 'left-0 mt-3']),
            src: _unref(brand).logos.sidebar.src,
            alt: _unref(brand).logos.sidebar.alt
          }, null, 10, _hoisted_14), [
            [_vShow, _unref(systemStore).system.sidebarExpanded]
          ]),
          _withDirectives(_createElementVNode("img", {
            class: _normalizeClass([_unref(brand).logos.sidebarCollapsed.class, 'left-0 mt-2']),
            src: _unref(brand).logos.sidebarCollapsed.src,
            alt: _unref(brand).logos.sidebarCollapsed.alt
          }, null, 10, _hoisted_15), [
            [_vShow, !_unref(systemStore).system.sidebarExpanded]
          ])
        ]),
        _createElementVNode("button", {
          onClick: _cache[4] || (_cache[4] = ($event: any) => (collapseSidebar())),
          class: "shadow-md border border-gray-200 bg-white text-dbBlue hover:bg-dbBlue hover:text-white p-1 opacity-0 rounded-full group-hover/sidebar:opacity-100 transition-all duration-50 ease-in-out absolute right-0 -mr-[14px] top-5 cursor-pointer dark:bg-gray-900 dark:text-gray-100 dark:border-gray-800 dark:hover:bg-indigo-500"
        }, [
          _createVNode(_unref(ChevronLeftIconMini), {
            class: _normalizeClass([{'rotate-180': !_unref(systemStore).system.sidebarExpanded}, 'pr-[2px] w-5 h-5 transition-all ease-in-out duration-200']),
            "aria-hidden": "true"
          }, null, 8, ["class"])
        ]),
        _createElementVNode("nav", _hoisted_16, [
          _createElementVNode("ul", _hoisted_17, [
            _createElementVNode("li", null, [
              _createElementVNode("ul", _hoisted_18, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navigation.value, (item) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: item.name
                  }, [
                    _createVNode(_component_router_link, {
                      to: item.to,
                      class: _normalizeClass([_ctx.$route.meta.module === item.module ? 'bg-indigo-900 text-white dark:bg-gray-800' : 'text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800', 'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'])
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), {
                          class: _normalizeClass([_ctx.$route.meta.module === item.module ? 'text-white' : 'text-indigo-200 group-hover:text-white dark:text-gray-400', 'h-6 w-6 shrink-0']),
                          "aria-hidden": "true"
                        }, null, 8, ["class"])),
                        _withDirectives(_createElementVNode("span", null, _toDisplayString(item.name), 513), [
                          [_vShow, _unref(systemStore).system.sidebarExpanded]
                        ])
                      ]),
                      _: 2
                    }, 1032, ["to", "class"])
                  ]))
                }), 128))
              ])
            ]),
            _createElementVNode("li", _hoisted_19, [
              _withDirectives(_createElementVNode("span", _hoisted_20, "Additional Resources", 512), [
                [_vShow, _unref(systemStore).system.sidebarExpanded]
              ]),
              _createElementVNode("ul", _hoisted_21, [
                _createElementVNode("li", null, [
                  _createElementVNode("a", {
                    href: _unref(brand).blogUrl,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    class: "group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                  }, [
                    _createVNode(_unref(NewspaperIcon), {
                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                      "aria-hidden": "true"
                    }),
                    _withDirectives(_createElementVNode("span", null, "Read Our Blog", 512), [
                      [_vShow, _unref(systemStore).system.sidebarExpanded]
                    ])
                  ], 8, _hoisted_22)
                ]),
                _createElementVNode("li", null, [
                  _createElementVNode("button", {
                    type: "button",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (openWebinarModal())),
                    class: "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                  }, [
                    _createVNode(_unref(PlayCircleIcon), {
                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                      "aria-hidden": "true"
                    }),
                    _withDirectives(_createElementVNode("span", null, "View Our Latest Webinar", 512), [
                      [_vShow, _unref(systemStore).system.sidebarExpanded]
                    ])
                  ])
                ]),
                _createElementVNode("li", null, [
                  _createElementVNode("button", {
                    type: "button",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (openNewsletterModal())),
                    class: "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800"
                  }, [
                    _createVNode(_unref(EnvelopeIcon), {
                      class: "h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400",
                      "aria-hidden": "true"
                    }),
                    _withDirectives(_createElementVNode("span", null, "Subscribe to Our Newsletter", 512), [
                      [_vShow, _unref(systemStore).system.sidebarExpanded]
                    ])
                  ])
                ])
              ])
            ])
          ])
        ])
      ])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass([_unref(systemStore).system.sidebarExpanded ? 'lg:pl-72' : 'lg:pl-[72px]', 'transition-all duration-200 ease-in-out'])
    }, [
      _createElementVNode("div", _hoisted_23, [
        _createElementVNode("button", {
          type: "button",
          class: "-m-2.5 p-2.5 text-gray-700 lg:hidden",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (sidebarOpen.value = true))
        }, [
          _cache[14] || (_cache[14] = _createElementVNode("span", { class: "sr-only" }, "Open sidebar", -1)),
          _createVNode(_unref(Bars3Icon), {
            class: "h-6 w-6",
            "aria-hidden": "true"
          })
        ]),
        _cache[16] || (_cache[16] = _createElementVNode("div", {
          class: "h-6 w-px bg-gray-900/10 lg:hidden dark:bg-white/5",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", _hoisted_24, [
          _createElementVNode("div", _hoisted_25, [
            _createVNode(_unref(Menu), {
              as: "div",
              class: "relative"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(MenuButton), { class: "-m-1.5 flex items-center p-1.5" }, {
                  default: _withCtx(() => [
                    _cache[15] || (_cache[15] = _createElementVNode("span", { class: "sr-only" }, "Open user menu", -1)),
                    _createElementVNode("div", _hoisted_26, [
                      _createVNode(_unref(UserIcon), { class: "h-5 w-5" })
                    ]),
                    _createElementVNode("span", _hoisted_27, [
                      _createElementVNode("span", _hoisted_28, _toDisplayString(displayName.value), 1),
                      _createVNode(_unref(ChevronDownIcon), {
                        class: "ml-2 h-5 w-5 text-gray-400",
                        "aria-hidden": "true"
                      })
                    ])
                  ]),
                  _: 1,
                  __: [15]
                }),
                _createVNode(_Transition, {
                  "enter-active-class": "transition ease-out duration-100",
                  "enter-from-class": "transform opacity-0 scale-95",
                  "enter-to-class": "transform opacity-100 scale-100",
                  "leave-active-class": "transition ease-in duration-75",
                  "leave-from-class": "transform opacity-100 scale-100",
                  "leave-to-class": "transform opacity-0 scale-95"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(MenuItems), { class: "absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-gray-900 dark:ring-white/5" }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(userNavigation, (item) => {
                          return _createVNode(_unref(MenuItem), {
                            key: item.name
                          }, {
                            default: _withCtx(({ active }) => [
                              _createVNode(_component_router_link, {
                                to: item.to,
                                class: _normalizeClass([active ? 'bg-gray-50 dark:bg-gray-800' : '', 'block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-white'])
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(item.name), 1)
                                ]),
                                _: 2
                              }, 1032, ["to", "class"])
                            ]),
                            _: 2
                          }, 1024)
                        }), 64))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _createElementVNode("main", _hoisted_29, [
        _createElementVNode("div", _hoisted_30, [
          _renderSlot(_ctx.$slots, "default")
        ])
      ])
    ], 2),
    _createVNode(WebinarModal, {
      ref_key: "webinarModal",
      ref: webinarModal
    }, null, 512),
    _createVNode(NewsletterModal, {
      ref_key: "newsletterModal",
      ref: newsletterModal
    }, null, 512)
  ]))
}
}

})